import React, {
  useState,
  useEffect,
} from 'react';
import {
  Box,
  Grid,
  Typography,
  Tabs,
  Tab,
  ToggleButton,
  ToggleButtonGroup,
  LinearProgress,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  FilterList as FilterIcon,
  EmojiEvents as AchievementIcon,
} from '@mui/icons-material';
import AchievementCard from './AchievementCard';

const CATEGORIES = [
  { value: 'all', label: 'All Achievements' },
  { value: 'events', label: 'Events' },
  { value: 'tournaments', label: 'Tournaments' },
  { value: 'games', label: 'Games' },
  { value: 'trackmania', label: 'TrackMania' },
  { value: 'skill', label: 'Skills' },
  { value: 'social', label: 'Social' }
];

const RARITIES = [
  { value: 'all', label: 'All Rarities' },
  { value: 'common', label: 'Common' },
  { value: 'rare', label: 'Rare' },
  { value: 'epic', label: 'Epic' },
  { value: 'legendary', label: 'Legendary' }
];

const AchievementGrid = ({ achievements = [], loading = false }) => {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedRarity, setSelectedRarity] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all'); // all, unlocked, locked
  const [sortBy, setSortBy] = useState('order'); // order, rarity, points, unlock_date
  const [filteredAchievements, setFilteredAchievements] = useState([]);
  
  // Statistics
  const [stats, setStats] = useState({
    total: 0,
    unlocked: 0,
    locked: 0,
    totalPoints: 0,
    earnedPoints: 0,
    completionRate: 0
  });

  useEffect(() => {
    console.log('=== AchievementGrid received achievements ===');
    console.log('Total achievements:', achievements.length);
    console.log('First 3 achievements (sample):', achievements.slice(0, 3));
    console.log('Full achievements data:', achievements);
    
    // Check for unlocked achievements
    const unlockedAchievements = achievements.filter(a => a.unlocked);
    console.log('Unlocked achievements:', unlockedAchievements);
    console.log('Number of unlocked:', unlockedAchievements.length);
    
    // Calculate statistics
    const total = achievements.length;
    const unlocked = achievements.filter(a => a.unlocked).length;
    const locked = total - unlocked;
    const totalPoints = achievements.reduce((sum, a) => sum + (a.points || 0), 0);
    const earnedPoints = achievements
      .filter(a => a.unlocked)
      .reduce((sum, a) => sum + (a.points || 0), 0);
    const completionRate = total > 0 ? (unlocked / total) * 100 : 0;

    console.log('Calculated stats:', {
      total,
      unlocked,
      locked,
      totalPoints,
      earnedPoints,
      completionRate
    });

    setStats({
      total,
      unlocked,
      locked,
      totalPoints,
      earnedPoints,
      completionRate
    });
  }, [achievements]);

  useEffect(() => {
    let filtered = [...achievements];

    // Filter by category
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(a => a.category === selectedCategory);
    }

    // Filter by rarity
    if (selectedRarity !== 'all') {
      filtered = filtered.filter(a => a.rarity === selectedRarity);
    }

    // Filter by status
    if (filterStatus === 'unlocked') {
      filtered = filtered.filter(a => a.unlocked);
    } else if (filterStatus === 'locked') {
      filtered = filtered.filter(a => !a.unlocked);
    }

    // Sort
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'rarity':
          const rarityOrder = { legendary: 4, epic: 3, rare: 2, common: 1 };
          return (rarityOrder[b.rarity] || 0) - (rarityOrder[a.rarity] || 0);
        case 'points':
          return (b.points || 0) - (a.points || 0);
        case 'unlock_date':
          if (!a.unlocked && !b.unlocked) return 0;
          if (!a.unlocked) return 1;
          if (!b.unlocked) return -1;
          return new Date(b.unlocked_at) - new Date(a.unlocked_at);
        case 'order':
        default:
          return (a.order || 0) - (b.order || 0);
      }
    });

    setFilteredAchievements(filtered);
  }, [achievements, selectedCategory, selectedRarity, filterStatus, sortBy]);

  if (loading) {
    return (
      <Box sx={{ py: 4, textAlign: 'center' }}>
        <Typography variant="h6" gutterBottom>Loading achievements...</Typography>
        <LinearProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Statistics Summary */}
      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <AchievementIcon color="primary" sx={{ fontSize: 32 }} />
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            Achievement Progress
          </Typography>
        </Box>
        
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 700, color: 'primary.main' }}>
                {stats.unlocked}/{stats.total}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Achievements Unlocked
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 700, color: 'secondary.main' }}>
                {stats.earnedPoints}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Points Earned
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 700, color: 'success.main' }}>
                {stats.completionRate.toFixed(1)}%
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Completion Rate
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 700, color: 'info.main' }}>
                {stats.locked}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                To Unlock
              </Typography>
            </Box>
          </Grid>
        </Grid>

        {/* Progress bar */}
        <Box sx={{ mt: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Overall Progress
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {stats.earnedPoints} / {stats.totalPoints} Points
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={stats.completionRate}
            sx={{
              height: 12,
              borderRadius: 6,
              backgroundColor: 'rgba(0,0,0,0.1)'
            }}
          />
        </Box>
      </Paper>

      {/* Filters */}
      <Paper elevation={1} sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <FilterIcon />
          <Typography variant="h6">Filters</Typography>
        </Box>

        <Grid container spacing={2}>
          {/* Category tabs */}
          <Grid item xs={12}>
            <Tabs
              value={selectedCategory}
              onChange={(e, newValue) => setSelectedCategory(newValue)}
              variant="scrollable"
              scrollButtons="auto"
            >
              {CATEGORIES.map(cat => (
                <Tab key={cat.value} label={cat.label} value={cat.value} />
              ))}
            </Tabs>
          </Grid>

          {/* Status filter */}
          <Grid item xs={12} sm={4}>
            <ToggleButtonGroup
              value={filterStatus}
              exclusive
              onChange={(e, newValue) => newValue && setFilterStatus(newValue)}
              fullWidth
              size="small"
            >
              <ToggleButton value="all">All</ToggleButton>
              <ToggleButton value="unlocked">Unlocked</ToggleButton>
              <ToggleButton value="locked">Locked</ToggleButton>
            </ToggleButtonGroup>
          </Grid>

          {/* Rarity filter */}
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth size="small">
              <InputLabel>Rarity</InputLabel>
              <Select
                value={selectedRarity}
                onChange={(e) => setSelectedRarity(e.target.value)}
                label="Rarity"
              >
                {RARITIES.map(rarity => (
                  <MenuItem key={rarity.value} value={rarity.value}>
                    {rarity.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Sort by */}
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth size="small">
              <InputLabel>Sort By</InputLabel>
              <Select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                label="Sort By"
              >
                <MenuItem value="order">Default Order</MenuItem>
                <MenuItem value="rarity">Rarity</MenuItem>
                <MenuItem value="points">Points</MenuItem>
                <MenuItem value="unlock_date">Unlock Date</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      {/* Achievement grid */}
      {filteredAchievements.length === 0 ? (
        <Paper elevation={1} sx={{ p: 4, textAlign: 'center' }}>
          <AchievementIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No achievements found
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Try adjusting your filters
          </Typography>
        </Paper>
      ) : (
        <>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <Typography variant="body1" color="text.secondary">
              Showing {filteredAchievements.length} achievement{filteredAchievements.length !== 1 ? 's' : ''}
            </Typography>
          </Box>
          
          <Grid container spacing={3}>
            {filteredAchievements.map((achievement) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={achievement.achievement_id || achievement.id || achievement._id}>
                <AchievementCard achievement={achievement} />
              </Grid>
            ))}
          </Grid>
        </>
      )}
    </Box>
  );
};

export default AchievementGrid;

